import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDataRefresh, ViewModelForm, ViewModelServiceContext} from '@xengage/gw-portals-viewmodel-react';
import { Contact, Person, Driver } from 'gw-capability-quoteandbind';
import PropTypes from 'prop-types';
import appConfig from 'app-config';
import _ from 'lodash';
import {EntityUtil} from '@xengage/gw-portals-util-js';
import {MLDriverVehicleUtil} from "ml-portals-util-react";
import Address from '../Models/Address';
import messages from './MLInsuredCandidatesComponent.messages';

import metadata from './MLInsuredCandidatesComponent.metadata.json5';
import styles from './MLInsuredCandidatesComponent.module.scss';
import { TranslatorContext } from '@jutro/legacy/locale';

const isQnB = appConfig.portalCode === 'QB';

function MLInsuredCandidatesComponent(props) {
    const {
        submissionVM,
        driverVM,
        vehicleOperatorVM,
        watercraftOperatorVM,
        otherPayorVM,
        driverType,
        setInsuredCandidateSelected,
        initPublicID,
        label,
        partialDriverAdditionFlag,
        onValueChange,
        viewModelService,
        showValidationErrors,
        isNavigationChange
    } = props;

    const {
        getRatedDriverType,
        getHouseholdDriverType,
        getExcludedDriverType,
        getInsureDriver
    } = MLDriverVehicleUtil(viewModelService);

    const { refreshData } = useDataRefresh();

    const translator = useContext(TranslatorContext);

    const [selectedInsuredCandidatePublicID, setSelectedInsuredCandidatePublicID] = useState(initPublicID);

    const lobDataModel = _.get(submissionVM, 'lobData', '');
    const bindDataModel = _.get(submissionVM, 'bindData', '');
    const paymentDetailsModel = _.get(submissionVM, 'paymentDetails_ML', '');
    const secondaryNamedInsured = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined);
    const driverDTOPath = 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.DriverDTO';
    const vehicleOperatorDTOPath = 'metlife.edge.capabilities.common.plugin.dto.PUPVehicleOperatorDTOML';
    const watercraftOperatorDTOPath = 'metlife.edge.capabilities.common.plugin.dto.PUPWatercraftOperatorDTOML';
    const otherPayorDTOPath = 'metlife.edge.capabilities.common.plugin.dto.PayorDTOML';

    const jobType = _.get(submissionVM, 'baseData.jobType.value.code');
    const POLICY_CHANGE = 'PolicyChange';

    const getAvailableInsuredCandidates = useCallback(() => {
        let availableCandidates;
        if (driverVM) {
            availableCandidates = lobDataModel.personalAuto.coverables.availableInsuredCandidates_Ext;
        } else if (otherPayorVM) {
            availableCandidates = jobType === POLICY_CHANGE ? paymentDetailsModel.otherPayorCandidates_ML : bindDataModel.paymentDetails.otherPayorCandidates_ML;
        } else {
            availableCandidates = lobDataModel.personalUmbrella.coverables.availableInsuredCandidates_Ext;
        }

        if (watercraftOperatorVM) {
            const existingContact = viewModelService.create(watercraftOperatorVM.value, 'pc', 'metlife.edge.capabilities.policycommon.insuredcandidate.dto.InsuredCandidateDTO_Ext');
            
            if (isNavigationChange && initPublicID && availableCandidates.value.findIndex((candidate) => candidate.publicID === existingContact.value.publicID) === -1) {
                availableCandidates.pushElement(existingContact);

                if (setInsuredCandidateSelected) {
                    setInsuredCandidateSelected(true);
                }
            }
        }


        availableCandidates.forEach((candidate) => { 
            if (candidate.value !== undefined && candidate.displayName.value === undefined) {
                candidate.displayName  = candidate?.person?.displayName?.value || candidate?.contact?.displayName?.value ;
            }
        });

        return availableCandidates;
    }, [bindDataModel, isNavigationChange, setInsuredCandidateSelected, initPublicID,watercraftOperatorVM ,viewModelService, driverVM, jobType, lobDataModel, otherPayorVM, paymentDetailsModel]);
    const availableInsuredCandidates = getAvailableInsuredCandidates();

    const createNewInsuredCandidate = useCallback((publicID, displayName) => {
        if (driverVM) {
            const newInsuredCandidate = {
                person: {
                    publicID
                },
                displayName
            };
            return viewModelService.create(newInsuredCandidate, 'pc', driverDTOPath);
        }

        if (otherPayorVM) {
            const newInsuredCandidate = {
                publicID,
                displayName
            };
            return viewModelService.create(newInsuredCandidate, 'pc', otherPayorDTOPath); 
        }
        
        const newInsuredCandidate = {
            contact: {
                publicID
            },
            displayName
        };
        if (vehicleOperatorVM) {
            return viewModelService.create(newInsuredCandidate, 'pc', vehicleOperatorDTOPath);
        }
        return viewModelService.create(newInsuredCandidate, 'pc', watercraftOperatorDTOPath);
    }, [driverVM, otherPayorVM, vehicleOperatorVM, viewModelService]);

    const filterAvailableInsuredCandidates = useCallback(() => {
        let list = [];
        if (availableInsuredCandidates.value) {
            list = availableInsuredCandidates.value.filter((insuredCandidate) => insuredCandidate !== undefined);
        }
         
        const newInsuredCandidateVMOption = createNewInsuredCandidate('new', translator(messages.AddNewInsuredCandidate));
        list.push(newInsuredCandidateVMOption.value);
        
        return list;
    }, [availableInsuredCandidates.value, createNewInsuredCandidate, translator]);

    const filteredAvailableInsuredCandidates = filterAvailableInsuredCandidates();

    const addNewDriverModel = useCallback(() => {
        const driverObj = {
            accidents: '0',
            violations: '0',
            tempID: EntityUtil.nextId()
        };
        driverObj.person = new Person({
            tempID: EntityUtil.nextId(),
            primaryAddress: submissionVM.baseData.policyAddress.value,
            middleName: null
        });
        driverObj.licenseState = driverObj.person.primaryAddress.state;

        driverObj.partialDriverAdditionFlag_ML = partialDriverAdditionFlag;
        driverObj.type_ML = driverType;
        driverObj.status_ML = driverType === getRatedDriverType() ? getInsureDriver() : undefined;
        driverObj.error = {
            message: null
        };
        driverObj.editMode_ML = true;
        driverObj.selected = true;

        return viewModelService.create(driverObj, 'pc', driverDTOPath);
    }, [driverType, getInsureDriver, getRatedDriverType, submissionVM.baseData.policyAddress.value, viewModelService, partialDriverAdditionFlag]);

    const setDriver = useCallback((existingDriver) => {
        _.set(driverVM, 'value', new Driver(existingDriver));
        
        _.set(driverVM, 'accidents.value', existingDriver.accidents || '0');
        _.set(driverVM, 'violations.value', existingDriver.violations || '0');
        _.set(driverVM, 'tempID.value', existingDriver.tempID || EntityUtil.nextId());

        if (jobType !== POLICY_CHANGE && submissionVM.quoteID.value && secondaryNamedInsured && secondaryNamedInsured.publicID === existingDriver.person.publicID) {
            _.set(driverVM, 'person.value', submissionVM.value.findPerson(submissionVM.value, secondaryNamedInsured) || {});
        } else {
            _.set(driverVM, 'person.value', new Person(existingDriver.person) || {});
        }
        if (!driverVM.person.publicID && !(driverVM.person.tempID && driverVM.person.tempID.value)) {
            _.set(driverVM, 'person.tempID.value', EntityUtil.nextId());
        }
        if (!(driverVM.person.primaryAddress && driverVM.person.primaryAddress.value)) {
            _.set(driverVM, 'person.primaryAddress.value', submissionVM.baseData.policyAddress.value);
        }
        driverVM.dateOfBirth.value = driverVM.person.dateOfBirth.value;

        _.set(driverVM, 'licenseState.value', existingDriver.licenseState || driverVM.person.primaryAddress.value.state);
        _.set(driverVM, 'partialDriverAdditionFlag_ML.value', partialDriverAdditionFlag);
        _.set(driverVM, 'type_ML.value', driverType);
        _.set(driverVM, 'value.error.message', null);
        _.set(driverVM, 'editMode_ML.value', true);
    }, [driverType, driverVM, jobType, secondaryNamedInsured, submissionVM, partialDriverAdditionFlag]);

    const addNewVehicleOperatorModel = useCallback(() => {
        const driverObj = {
            driverType: driverType,
            error: {
                message: null
            },
            editMode_ML: true,
            contact: {
                isGenderRequired: true
            },
            isRelationshipRequired: true,
            isDriverStatusForAddingRequired: true,
            isDriverTypeRequired: true,
            isAgeFirstLicensedRequired: true
        };
        switch(driverType){
            case 'rated':
                driverObj.driverStatusForAdding = 'insure_driver';
                driverObj.contact.isMaritalStatusRequired = true;
                driverObj.contact.isDateOfBirthRequired = true;
                driverObj.isAgeFirstLicensedRequired = true;
                break;
            case 'excluded':
                driverObj.contact.isDateOfBirthRequired = true;
                driverObj.isAgeFirstLicensedRequired = false;
                break;
            default:
                driverObj.contact.isMaritalStatusRequired = false;
                driverObj.contact.isDateOfBirthForNonDriverRequired = true;
                driverObj.isAgeFirstLicensedRequired = false;
        }

        return viewModelService.create(driverObj, 'pc', vehicleOperatorDTOPath);
    }, [driverType, viewModelService]);

    const setVehicleOperator = useCallback((existingDriver) => {
        _.set(vehicleOperatorVM, 'value', existingDriver);
        
        _.set(vehicleOperatorVM, 'driverType.value', driverType);
        _.set(vehicleOperatorVM, 'value.error.message', null);
        _.set(vehicleOperatorVM, 'editMode_ML.value', true);

        if (jobType !== POLICY_CHANGE && submissionVM.quoteID.value && secondaryNamedInsured && secondaryNamedInsured.publicID === existingDriver.contact.publicID) {
            _.set(vehicleOperatorVM, 'contact.value', submissionVM.value.findPerson(submissionVM.value, secondaryNamedInsured) || {});
        } else {
            _.set(vehicleOperatorVM, 'contact.value', new Contact(existingDriver.contact) || {});
        }

        if (!vehicleOperatorVM.contact.isGenderRequired.value) {
            _.set(vehicleOperatorVM, 'contact.isGenderRequired.value', true);
        }
        
        _.set(vehicleOperatorVM, 'isRelationshipRequired.value', existingDriver.isRelationshipRequired || true);
        _.set(vehicleOperatorVM, 'isDriverStatusForAddingRequired.value', existingDriver.isDriverStatusForAddingRequired || true);
        _.set(vehicleOperatorVM, 'isDriverTypeRequired.value', existingDriver.isDriverTypeRequired || true);
        _.set(vehicleOperatorVM, 'isAgeFirstLicensedRequired.value', existingDriver.isAgeFirstLicensedRequired || true);

        switch(driverType){
            case getRatedDriverType():
                _.set(vehicleOperatorVM, 'driverStatusForAdding.value', getInsureDriver());
                _.set(vehicleOperatorVM, 'contact.isMaritalStatusRequired.value', true);
                _.set(vehicleOperatorVM, 'contact.isDateOfBirthRequired.value', true);
                _.set(vehicleOperatorVM, 'contact.isDateOfBirthForNonDriverRequired.value', false);
                _.set(vehicleOperatorVM, 'isAgeFirstLicensedRequired.value', true);
                break;
            case getExcludedDriverType():
                _.set(vehicleOperatorVM, 'contact.isDateOfBirthRequired.value', true);
                _.set(vehicleOperatorVM, 'contact.isDateOfBirthForNonDriverRequired.value', false);
                _.set(vehicleOperatorVM, 'isAgeFirstLicensedRequired.value', false);
                break;
            default:
                _.set(vehicleOperatorVM, 'contact.isMaritalStatusRequired.value', true);
                _.set(vehicleOperatorVM, 'contact.isDateOfBirthForNonDriverRequired.value', true);
                _.set(vehicleOperatorVM, 'contact.isDateOfBirthRequired.value', false);
                _.set(vehicleOperatorVM, 'isAgeFirstLicensedRequired.value', false);
        }
        
    }, [vehicleOperatorVM, driverType, jobType, submissionVM, secondaryNamedInsured, getRatedDriverType, getInsureDriver, getExcludedDriverType]);

    const addNewWatercraftOperatorModel = useCallback(() => {

        const newVehicleOperator = {
            contact: new Contact({
                isGenderRequired: true
            })
        };
        const operatorVM = viewModelService.create(newVehicleOperator, 'pc', vehicleOperatorDTOPath);
        
        const newWatercraftOperator = {
            fixedID: undefined,
            contact: new Contact({
                isGenderRequired: true
            })
        };
        const watercraftOperator = viewModelService.create(newWatercraftOperator, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PUPWatercraftOperatorDTOML');

        watercraftOperator.contact.value = operatorVM.contact.value;
        watercraftOperator.relationshipToInsured.value = operatorVM.relationshipToInsured.value;
        operatorVM.contact.isDateOfBirthRequired = false;
        operatorVM.contact.isDateOfBirthForNonDriverRequired = true;

        watercraftOperator.value.isEditing = true;
        return watercraftOperator;
    }, [viewModelService]);

    const setWatercraftOperator = useCallback((existingDriver) => {
        _.set(watercraftOperatorVM, 'value', existingDriver);
        
        _.set(watercraftOperatorVM, 'value.error.message', null);
        _.set(watercraftOperatorVM, 'editMode_ML.value', true);

        if (jobType !== POLICY_CHANGE && submissionVM.quoteID.value && secondaryNamedInsured && secondaryNamedInsured.publicID === existingDriver.contact.publicID) {
            _.set(watercraftOperatorVM, 'contact.value', submissionVM.value.findPerson(submissionVM.value, secondaryNamedInsured) || {});
        } else {
            _.set(watercraftOperatorVM, 'contact.value', new Contact(existingDriver.contact) || {});
        }

        if (!watercraftOperatorVM.contact.isGenderRequired.value) {
            _.set(watercraftOperatorVM, 'contact.isGenderRequired.value', true);
        }

        _.set(watercraftOperatorVM, 'isRelationshipRequired.value', existingDriver.isRelationshipRequired || true);

        switch(driverType){
            case getHouseholdDriverType():
                _.set(watercraftOperatorVM, 'contact.isDateOfBirthRequired.value', false);
                _.set(watercraftOperatorVM, 'contact.isDateOfBirthForNonDriverRequired.value', true);
                break;
            default:
                _.set(watercraftOperatorVM, 'contact.isDateOfBirthRequired.value', true);
                _.set(watercraftOperatorVM, 'contact.isDateOfBirthForNonDriverRequired.value', false);
        }
        
    }, [watercraftOperatorVM, jobType, submissionVM, secondaryNamedInsured, driverType, getHouseholdDriverType]);

    const addNewOtherPayorModel = useCallback(() => {
        const newOtherPayor = {
            address: viewModelService.create(new Address(), 'pc', 'edge.capabilities.address.dto.AddressDTO').value
        };
        const otherPayor = viewModelService.create(newOtherPayor, 'pc', otherPayorDTOPath);

        return otherPayor;
    }, [viewModelService]);

    const setOtherPayor = useCallback((existingDriver) => {
        _.set(otherPayorVM, 'value', existingDriver);
        _.set(otherPayorVM, 'updated.value', true);
    }, [otherPayorVM]);

    const onAddInsuredCandidate = useCallback((value) => {

        if (setInsuredCandidateSelected) {
            setInsuredCandidateSelected(true);
        }

        setSelectedInsuredCandidatePublicID(value);
        let newSelectedInsuredCandidate;
        let selectedInsuredCandidate;
        if (driverVM) {
            if (availableInsuredCandidates.value) {
                selectedInsuredCandidate = availableInsuredCandidates.value.find((insuredCandidate) => insuredCandidate.person.publicID === value);
            }
            if (selectedInsuredCandidate) {
                newSelectedInsuredCandidate = viewModelService.create(selectedInsuredCandidate, 'pc', driverDTOPath);
            } else {
                newSelectedInsuredCandidate = addNewDriverModel();
            }
            setDriver(newSelectedInsuredCandidate.value);
        } else if (vehicleOperatorVM) {
            if (availableInsuredCandidates.value) {
                selectedInsuredCandidate = availableInsuredCandidates.value.find((insuredCandidate) => insuredCandidate.contact.publicID === value);
            }
            if (selectedInsuredCandidate) {
                newSelectedInsuredCandidate = viewModelService.create(selectedInsuredCandidate, 'pc', vehicleOperatorDTOPath);
            } else {
                newSelectedInsuredCandidate = addNewVehicleOperatorModel();
            }
            setVehicleOperator(newSelectedInsuredCandidate.value);
        } else if (watercraftOperatorVM) {
            if (availableInsuredCandidates.value) {
                selectedInsuredCandidate = availableInsuredCandidates.value.find((insuredCandidate) => insuredCandidate.contact.publicID === value);
            }
            if (selectedInsuredCandidate) {
                newSelectedInsuredCandidate = viewModelService.create(selectedInsuredCandidate, 'pc', watercraftOperatorDTOPath);
            } else {
                newSelectedInsuredCandidate = addNewWatercraftOperatorModel();
            }
            setWatercraftOperator(newSelectedInsuredCandidate.value);
        } else {
            if (availableInsuredCandidates.value) {
                selectedInsuredCandidate = availableInsuredCandidates.value.find((insuredCandidate) => insuredCandidate.publicID === value);
            }
            if (selectedInsuredCandidate) {
                newSelectedInsuredCandidate = viewModelService.create(selectedInsuredCandidate, 'pc', otherPayorDTOPath);
            } else {
                newSelectedInsuredCandidate = addNewOtherPayorModel();
            }
            setOtherPayor(newSelectedInsuredCandidate.value);
        }

        refreshData();

        if (onValueChange) {
            onValueChange();
        }

    }, [setInsuredCandidateSelected, driverVM, vehicleOperatorVM, watercraftOperatorVM, refreshData, onValueChange, availableInsuredCandidates.value, setDriver, viewModelService, addNewDriverModel, setVehicleOperator, addNewVehicleOperatorModel, setWatercraftOperator, addNewWatercraftOperatorModel, setOtherPayor, addNewOtherPayorModel]);

    const getCode = useCallback((insuredCandidate) => {
        if (driverVM) {
            return insuredCandidate.person.publicID;
        }
        if (otherPayorVM) {
            return insuredCandidate.publicID;
        }
        return insuredCandidate.contact.publicID;
    }, [driverVM, otherPayorVM]);

    const formatAvailableValues = (insuredCandidates) => {
        if (!insuredCandidates) {
            return [];
        }
        return insuredCandidates.map((insuredCandidate) => ({
            code: getCode(insuredCandidate),
            name: translator({
                id: insuredCandidate.displayName,
                defaultMessage: insuredCandidate.displayName
            })
        })).sort((a, b) => {
            if (a.name === messages.AddNewInsuredCandidate.defaultMessage) {
                return 1;
            }
            return a.name.toUpperCase().localeCompare(b.name.toUpperCase())
        });
    };

    const overrideProps = {
        insuredCandidateDropdown: {
            availableValues: formatAvailableValues(filteredAvailableInsuredCandidates),
            value: selectedInsuredCandidatePublicID,
            onValueChange: (value) => {
                if (value !== selectedInsuredCandidatePublicID) {
                    onAddInsuredCandidate(value);
                }
            },
            label: label ? translator(label) : undefined,
            required: true,
        },
        insuredCandidatesDropdown:{
            'aria-labelledby': isQnB ? "Select Driver Dropdown" : ""
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={driverVM || vehicleOperatorVM || watercraftOperatorVM || otherPayorVM}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            showErrors={showValidationErrors}
        />
    );
}

MLInsuredCandidatesComponent.propTypes = {
    submissionVM: PropTypes.shape({}).isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
};

MLInsuredCandidatesComponent.defaultProps = {
    driverType: 'rated',
    partialDriverAdditionFlag: false,
    showValidationErrors: false
};

export default MLInsuredCandidatesComponent;
